import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getComplaints, updateComplaintStatus } from '@/services/api';
import { FileText, AlertCircle, Clock, CheckCircle2, Paperclip, Image, FileIcon, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminComplaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const data = await getComplaints();
      setComplaints(data);
    } catch (error) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (complaintId, status) => {
    try {
      await updateComplaintStatus(complaintId, status);
      toast.success('Complaint status updated');
      fetchComplaints();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { icon: AlertCircle, variant: 'destructive', label: 'Open' },
      in_progress: { icon: Clock, variant: 'secondary', label: 'In Progress' },
      resolved: { icon: CheckCircle2, variant: 'default', label: 'Resolved' },
      closed: { icon: CheckCircle2, variant: 'outline', label: 'Closed' }
    };
    const item = config[status] || config.open;
    const Icon = item.icon;
    return (
      <Badge variant={item.variant}>
        <Icon className="h-3 w-3 mr-1" />
        {item.label}
      </Badge>
    );
  };

  const filteredComplaints = filterStatus === 'all' 
    ? complaints 
    : complaints.filter(c => c.status === filterStatus);

  const getAttachmentUrl = (filename) => `${BACKEND_URL}/api/uploads/${filename}?auth=${localStorage.getItem('token')}`;

  const isImageFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };

  const handleViewAttachments = (complaint) => {
    setSelectedComplaint(complaint);
  };

  const handleDownload = (filename) => {
    window.open(getAttachmentUrl(filename), '_blank');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-complaints">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Complaints</h1>
          <p className="text-slate-600">Manage resident complaints</p>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4">
        {filteredComplaints.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No complaints found</p>
            </CardContent>
          </Card>
        ) : (
          filteredComplaints.map((complaint) => (
            <Card key={complaint.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="complaint-card">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="h-10 w-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-900">{complaint.title}</h3>
                          {getStatusBadge(complaint.status)}
                          {complaint.attachments && complaint.attachments.length > 0 && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Paperclip className="h-3 w-3 mr-1" />
                              {complaint.attachments.length} file{complaint.attachments.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mb-3">
                          By {complaint.user_name} | Unit {complaint.unit_number} | {complaint.category} | Priority: {complaint.priority}
                        </p>
                        <p className="text-slate-600 mb-3">{complaint.description}</p>
                        <div className="flex items-center gap-4 flex-wrap">
                          <p className="text-xs text-slate-500">
                            Submitted: {new Date(complaint.created_at).toLocaleDateString()}
                          </p>
                          {complaint.attachments && complaint.attachments.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewAttachments(complaint)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid="view-attachments-btn"
                            >
                              <Paperclip className="h-3 w-3 mr-1" />
                              View Attachments
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 md:w-48">
                    <Select
                      value={complaint.status}
                      onValueChange={(value) => handleStatusUpdate(complaint.id, value)}
                    >
                      <SelectTrigger data-testid="complaint-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Attachments Dialog */}
      <Dialog open={!!selectedComplaint} onOpenChange={() => setSelectedComplaint(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5" />
              Attachments for: {selectedComplaint?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Submitted by {selectedComplaint?.user_name} | Unit {selectedComplaint?.unit_number}
            </p>
            <div className="grid gap-4">
              {selectedComplaint?.attachments?.map((filename, idx) => (
                <div key={idx} className="border rounded-lg p-4 bg-slate-50">
                  {isImageFile(filename) ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Image className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium">Image {idx + 1}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPreviewImage(getAttachmentUrl(filename))}
                            data-testid={`preview-image-${idx}`}
                          >
                            Preview
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(filename)}
                            data-testid={`download-attachment-${idx}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Open
                          </Button>
                        </div>
                      </div>
                      <img
                        src={getAttachmentUrl(filename)}
                        alt={`Attachment ${idx + 1}`}
                        className="max-h-48 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setPreviewImage(getAttachmentUrl(filename))}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4 text-red-600" />
                        <span className="text-sm font-medium">PDF Document {idx + 1}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(filename)}
                        data-testid={`download-attachment-${idx}`}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Open PDF
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {(!selectedComplaint?.attachments || selectedComplaint.attachments.length === 0) && (
              <p className="text-center text-slate-500 py-8">No attachments for this complaint</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setPreviewImage(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AdminComplaints;